import React, { useEffect, useState } from 'react';
import DashboardShell from '@/components/DashboardShell';
import api from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import AdminTable from '@/components/AdminTable';

const EMPTY_FORM = { name: '', description: '', active: true };

export default function ListingTypes() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formDialog, setFormDialog] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchTypes = () => {
    setLoading(true);
    api.get('/admin/listing-types')
      .then(r => setTypes(r.data))
      .catch(() => toast.error('Failed to load listing types'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchTypes(); }, []);

  const openCreate = () => { setForm(EMPTY_FORM); setFormDialog('create'); };
  const openEdit = (t) => { setForm({ name: t.name, description: t.description || '', active: t.active }); setFormDialog(t); };

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      if (formDialog === 'create') {
        await api.post('/admin/listing-types', form);
        toast.success('Listing type created');
      } else {
        await api.put(`/admin/listing-types/${formDialog.id}`, form);
        toast.success('Listing type updated');
      }
      setFormDialog(null);
      fetchTypes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/listing-types/${deleteId}`);
      toast.success('Listing type deleted');
      setDeleteId(null);
      fetchTypes();
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DashboardShell title="Listing Types">
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{types.length} type{types.length !== 1 ? 's' : ''}</p>
          <Button
            onClick={openCreate}
            style={{ backgroundColor: 'var(--text)', color: 'var(--bg)', borderRadius: '10px' }}
            data-testid="admin-create-type-button"
          >
            <Plus size={15} className="mr-1.5" /> Add type
          </Button>
        </div>

        <AdminTable
          data={types}
          loading={loading}
          dataTestId="admin-listing-types-table"
          emptyTitle="No listing types yet"
          emptySubtitle="Click “Add type” to create your first one."
          columns={[
            { key: 'name', label: 'Name', render: (t) => <p className="font-medium" style={{ color: 'var(--text)' }}>{t.name}</p> },
            { key: 'description', label: 'Description', render: (t) => <p className="text-xs line-clamp-1" style={{ color: 'var(--text-muted)' }}>{t.description || '—'}</p> },
            {
              key: 'active', label: 'Active',
              render: (t) => (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${t.active ? 'bg-[#D1FAE5] text-[#065F46]' : 'bg-[#F3F4F6] text-[#374151]'}`}>
                  {t.active ? 'Active' : 'Inactive'}
                </span>
              ),
            },
            {
              key: 'actions', label: '', width: '100px',
              render: (t) => (
                <div className="flex items-center gap-2 justify-end">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(t)}>
                    <Pencil size={13} />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDeleteId(t.id)} style={{ color: 'var(--danger)' }}>
                    <Trash2 size={13} />
                  </Button>
                </div>
              ),
            },
          ]}
        />
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={!!formDialog} onOpenChange={() => setFormDialog(null)}>
        <DialogContent style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>
              {formDialog === 'create' ? 'Add listing type' : 'Edit listing type'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>Name <span style={{ color: 'var(--gold)' }}>·</span></label>
              <Input
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Original artwork"
                style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>Description</label>
              <Textarea
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Short description…"
                rows={2}
                style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={form.active}
                onCheckedChange={v => setForm(p => ({ ...p, active: v }))}
                id="type-active"
              />
              <label htmlFor="type-active" className="text-sm cursor-pointer" style={{ color: 'var(--text)' }}>Active</label>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setFormDialog(null)} disabled={submitting} style={{ borderRadius: '10px' }}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              style={{ backgroundColor: 'var(--text)', color: 'var(--bg)', borderRadius: '10px' }}
            >
              {submitting ? 'Saving…' : formDialog === 'create' ? 'Create' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>Delete listing type?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: 'var(--text-muted)' }}>
              This will permanently delete the listing type.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ borderRadius: '10px' }}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              style={{ backgroundColor: 'var(--danger)', color: 'white', borderRadius: '10px' }}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardShell>
  );
}
